import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Droplets, User } from "lucide-react";
import { NAV, CONTACT } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import LanguageToggle from "@/components/LanguageToggle";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      style={{ top: "var(--cf-ribbon-h, 0px)" }}
      data-testid="site-header"
    >
      {!scrolled && (
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[140%] bg-gradient-to-b from-abyss/75 via-abyss/35 to-transparent" aria-hidden />
      )}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-2 group"
          data-testid="logo-button"
        >
          <Droplets className="w-6 h-6 text-aqua transition-transform duration-300 group-hover:rotate-12" />
          <span className="flex flex-col items-start leading-none">
            <span className="font-display font-extrabold text-lg tracking-tight uppercase">Capital Flow</span>
            <span className="hidden text-[9px] font-semibold uppercase tracking-[0.18em] text-aqua/80 lg:block">Purity in Motion · La pureté en mouvement</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="relative text-sm text-white/70 hover:text-white transition-colors duration-300 group"
              data-testid={`nav-${item.key}`}
            >
              {t.nav[item.key]}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-aqua transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <a
            href="/blog"
            className="relative text-sm text-white/70 hover:text-white transition-colors duration-300 group"
            data-testid="nav-tips"
          >
            {t.nav.tips}
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-aqua transition-all duration-300 group-hover:w-full" />
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageToggle className="hidden sm:flex" />
          <Link
            to={user ? "/portal" : "/login"}
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors duration-300 hover:border-aqua hover:text-aqua"
            data-testid="header-account-link"
          >
            <User className="h-4 w-4" />
            {user ? t.header.account : t.header.signin}
          </Link>
          <button
            onClick={() => scrollTo("#order")}
            className="hidden sm:inline-flex items-center rounded-full bg-aqua px-6 py-2.5 text-sm font-bold text-abyss transition-transform duration-300 hover:scale-105 active:scale-95 glow-aqua"
            data-testid="header-order-button"
          >
            {t.header.order}
          </button>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden glass border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  onClick={() => {
                    scrollTo(item.href);
                    setOpen(false);
                  }}
                  className="text-left text-lg font-display font-bold text-white/80"
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <a
                href="/blog"
                onClick={() => setOpen(false)}
                className="text-left text-lg font-display font-bold text-white/80"
                data-testid="mobile-nav-tips"
              >
                {t.nav.tips}
              </a>
              <div className="flex items-center justify-between pt-2">
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="text-aqua text-sm"
                  data-testid="mobile-call-link"
                >
                  {CONTACT.phone}
                </a>
                <LanguageToggle />
              </div>
              <Link
                to={user ? "/portal" : "/login"}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm font-semibold text-white/80"
                data-testid="mobile-account-link"
              >
                <User className="h-4 w-4" /> {user ? t.header.account : t.header.signin}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
